import { ChangeDetectorRef, Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { ValidatorService } from '../../../core/services/validator.service';
import { Definition } from '../../../core/store/definitions/definition.interface';
import { Definitions } from '../../../core/store/definitions/definitions.interface';
import { FlordersFilter } from '../../../core/interfaces/florders-filter.interface';
import { Destroyable } from '@dlw-int-mwi/eps-common';
import { takeUntil } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';

export interface FlorderFilterForm {
  florderDateFrom: FormControl<string>;
  florderDateTo: FormControl<string>;
  unloadingDateFrom: FormControl<string>;
  unloadingDateTo: FormControl<string>;
  packingIds: FormControl<string[]>;
  froms: FormControl<string[]>;
  tos: FormControl<string[]>;
  incoterms: FormControl<string[]>;
  shipTos: FormControl<string[]>;
  etmShippingNumber: FormControl<string>;
  salesOrderNumber: FormControl<string>;
  etmDocumentNumber: FormControl<string>;
  senderReferenceNumber: FormControl<string>;
  customerReferenceNumber: FormControl<string>;
  moreSearchOptions: FormControl<boolean>;
  florderDefinitiveDateFrom: FormControl<string>;
  florderDefinitiveDateTo: FormControl<string>;
}

@Directive()
export abstract class FlorderFilterComponent<FILTERFORM extends FlorderFilterForm> extends Destroyable implements OnInit, OnChanges {
  private initialized: boolean = false;

  // @ts-ignore
  filterForm: FormGroup<FILTERFORM>;
  transport: { pickup: Definition; eps: Definition };
  showAdvanced: boolean = false;
  @Input() definitions: Definitions;
  @Input() isLoading: boolean;
  @Input() isBusy: boolean;
  @Input() filter: FlordersFilter;
  @Input() shipTos: Definition[];
  @Output() resetFilterClicked = new EventEmitter<void>();

  showDatesIn2rows$ = LayoutBreakpoints.matches(this.breakpointObserver, ...LayoutBreakpoints.tooSmallFor2Fields);

  constructor(protected formBuilder: UntypedFormBuilder, protected validatorService: ValidatorService, private breakpointObserver: BreakpointObserver, cd: ChangeDetectorRef) {
    super();
    // listen to width changes, decide if from-to date fields are shown next to each other or not
    this.showDatesIn2rows$.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(showDatesIn2rows => {
      cd.markForCheck();
    });
  }

  ngOnInit(): void {
    this.createForm();
    this.initialized = true;
    this.showAdvanced = this.filter && this.filter.moreSearchOptions;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.initialized && changes.filter) {
      this.createForm();
      this.showAdvanced = this.filter && this.filter.moreSearchOptions;
    }
    if (changes.definitions) {
      this.setTypes();
    }
    if (changes.isLoading || changes.isBusy) {
      (this.isLoading || this.isBusy) ? this.filterForm?.disable() : this.filterForm?.enable();
    }
  }

  setTypes(): void {
    this.transport = {
      eps: this.definitions.transport.type.find(def => def.id.toLowerCase() === 'z1'),
      pickup: this.definitions.transport.type.find(def => def.id.toLowerCase() === 'z2'),
    };
  }

  createForm() {
    const controlsConfig = this.getControlsConfig();
    this.filterForm = this.formBuilder.group(controlsConfig);
  }

  protected getControlsConfig() {
    return {
      florderDateFrom: [this.filter.florderDateFrom, [this.validatorService.datePatternValidator]],
      florderDateTo: [this.filter.florderDateTo, [this.validatorService.datePatternValidator, this.validatorService.dateSameOrAfter('orderDateFrom')]],
      unloadingDateFrom: [this.filter.unloadingDateFrom, [this.validatorService.datePatternValidator]],
      unloadingDateTo: [this.filter.unloadingDateTo, [this.validatorService.datePatternValidator, this.validatorService.dateSameOrAfter('unloadingDateFrom')]],
      packingIds: [this.filter.packingIds],
      froms: [this.filter.froms],
      tos: [this.filter.tos],
      incoterms: [this.filter.incoterms],
      shipTos: [this.filter.shipTos],
      etmShippingNumber: [this.filter.etmShippingNumber],
      salesOrderNumber: [this.filter.salesOrderNumber],
      etmDocumentNumber: [this.filter.etmDocumentNumber],
      senderReferenceNumber: [this.filter.senderReferenceNumber],
      customerReferenceNumber: [this.filter.customerReferenceNumber],
      moreSearchOptions: [this.filter.moreSearchOptions],
      florderDefinitiveDateFrom: [this.filter.florderDefinitiveDateFrom, [this.validatorService.datePatternValidator]],
      florderDefinitiveDateTo: [
        this.filter.florderDefinitiveDateTo,
        [this.validatorService.datePatternValidator, this.validatorService.dateSameOrAfter('florderDefinitiveDateFrom')],
      ],
    };
  }

  filterClicked(eventEmitter: EventEmitter<{ value: FlordersFilter; valid: boolean }>): void {
    if (!this.isLoading && !this.isBusy) {
      eventEmitter.emit({
        value: {
          ...this.filterForm.value,
          pageSize: this.filter.pageSize,
          moreSearchOptions: this.showAdvanced,
        },
        valid: this.filterForm.valid,
      });
    }
  }

  resetFilter() {
    if (!this.isLoading && !this.isBusy) {
      this.resetFilterClicked.emit();
    }
  }
}
